import './calendar.css'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
	DateSelectArg,
	DayCellContentArg,
	DayHeaderContentArg,
	EventChangeArg,
	EventClickArg,
	EventContentArg,
	DateSpanApi,
} from '@fullcalendar/core/index.js'
import esLocale from '@fullcalendar/core/locales/es'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import multiMonthPlugin from '@fullcalendar/multimonth'
import FullCalendar from '@fullcalendar/react'
import rrulePlugin from '@fullcalendar/rrule'
import timeGridPlugin from '@fullcalendar/timegrid'
import { Cloud } from 'lucide-react'
import { useSelector } from 'react-redux'

import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { Card } from '@/shared/components/molecules/Card/Card'
import DesktopOnlyModal from '@/shared/components/molecules/DesktopOnlyModal/DesktopOnlyModal'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'
import { capitalizeFirstLetter } from '@/shared/utils/helpers'
import { getDateFromMinutes } from '@/shared/utils/time'

import CalendarNav from './CalendarNav'
import { EventEditForm } from './EventEditForm'
import { EventView } from './EventView'
import { findStoredEventForChange } from './utils/findStoredEventForChange'
import { CalendarEvent, earliestTime, latestTime } from '../../context/Data'
import { useEvents } from '../../context/EventContext'
import { applyOverlapResolution as applySharedOverlapResolution } from '../../utils/applyOverlapResolution'
import {
	findOverlappingEvents,
	OverlapDetails,
} from '../../utils/priorityCalculator'
import { mergeRRulePreserveUntil } from '../../utils/rruleMerge'
import { EventAddForm } from '../EventAddForm/EventAddForm'
import { MultiOverlapWarningModal } from '../MultiOverlapWarningModal/MultiOverlapWarningModal'
import { OverlapConfirmationModal } from '../OverlapConfirmationModal/OverlapConfirmationModal'
import { SlotTypeSelectionModal } from '../SlotTypeSelection/SlotTypeSelectionModal'
import {
	SmartSlotTypeSelectionModal,
	WeatherSmartSlotStepper,
	OccupancySmartSlotStepper,
} from '../SmartSlots'

type EventItemProps = {
	info: EventContentArg
}

type DayHeaderProps = {
	info: DayHeaderContentArg
}

type DayRenderProps = {
	info: DayCellContentArg
}

// Transform CalendarEvent to FullCalendar format with extendedProps
function transformEventForFullCalendar(event: CalendarEvent) {
	return {
		id: event.id,
		title: event.title,
		start: event.start,
		end: event.end,
		backgroundColor: event.backgroundColor,
		borderColor: 'black',
		// These need to be at root level for FullCalendar to handle recurrence
		rrule: event.rrule,
		duration: event.duration,
		extendedProps: {
			description: event.description,
			timeslotId: event.timeslotId,
			timeslotContentId: event.timeslotContentId,
			playlists: event.playlists,
			repeatDays: event.repeatDays,
			startTime: event.startTime,
			volume: event.volume,
			slotType: event.slotType,
			smartSlotConfig: event.smartSlotConfig,
			timeslotName: event.timeslotName,
			timeslotContents: event.timeslotContents,
			gradientColors: event.gradientColors, // This is the key - gradient colors go here
		},
	}
}

export default function Calendar({ readOnly = false }: { readOnly?: boolean }) {
	const { t } = useTranslation()
	const { language } = useSelector((state: RootState) => state.ui)
	const currentAccount = useCurrentAccount()
	const [isMobile, setIsMobile] = useState(() => {
		if (typeof window === 'undefined') return false
		return window.matchMedia('(max-width: 1023px)').matches
	})

	// Flag to control whether to skip the slot type selection modal
	const SKIP_SLOT_TYPE_SELECTION = !currentAccount?.hasSmartSlotsFeature()

	// Determine FullCalendar locale based on current language
	const calendarLocale = useMemo(() => {
		if (language?.startsWith('es')) {
			return esLocale
		}
		return undefined // Default to English
	}, [language])

	// Determine initial view based on screen size (mobile = day, desktop = week)
	const initialCalendarView = useMemo(() => {
		return isMobile ? 'timeGridDay' : 'timeGridWeek'
	}, [isMobile])

	const {
		events,
		updateEvent,
		addEvent,
		deleteEvent,
		setEventAddOpen,
		setEventEditOpen,
		setEventViewOpen,
		setSlotTypeModalOpen,
		setWeatherSlotStepperOpen,
		setOccupancySlotStepperOpen,
		setSmartSlotTypeModalOpen,
		setSelectedSlotType,
		setSelectedSmartType,
		slotTypeModalOpen,
		smartSlotTypeModalOpen,
		weatherSlotStepperOpen,
		occupancySlotStepperOpen,
		overlapConfirmationModalOpen,
		setOverlapConfirmationModalOpen,
		overlapDetails,
		setOverlapDetails,
		pendingSlotStart,
		setPendingSlotStart,
		pendingSlotEnd,
		setPendingSlotEnd,
	} = useEvents()

	const calendarRef = useRef<FullCalendar>(null)
	const [viewedDate, setViewedDate] = useState(new Date())
	const [selectedStart, setSelectedStart] = useState(new Date())
	const [selectedEnd, setSelectedEnd] = useState(new Date())
	const [selectedOldEvent, setSelectedOldEvent] = useState<
		CalendarEvent | undefined
	>()
	const [selectedEvent, setSelectedEvent] = useState<
		CalendarEvent | undefined
	>()
	const [isDrag, setIsDrag] = useState(false)
	const [isTimeLockedFromOverlap, setIsTimeLockedFromOverlap] = useState(false)
	const [showDesktopOnlyWarning, setShowDesktopOnlyWarning] = useState(false)

	const [multiOverlapWarningOpen, setMultiOverlapWarningOpen] = useState(false)
	const [multiOverlapDetails, setMultiOverlapDetails] = useState<
		OverlapDetails[]
	>([])

	const showDesktopOnlyModal = () => {
		setShowDesktopOnlyWarning(true)
	}

	useEffect(() => {
		if (typeof window === 'undefined') return
		const mediaQuery = window.matchMedia('(max-width: 1023px)')

		const handleViewportChange = (event: MediaQueryListEvent) => {
			setIsMobile(event.matches)
			if (event.matches) {
				const calendarApi = calendarRef.current?.getApi()
				if (calendarApi && calendarApi.view.type !== 'timeGridDay') {
					calendarApi.changeView('timeGridDay')
				}
			}
		}

		setIsMobile(mediaQuery.matches)
		if (mediaQuery.matches) {
			const calendarApi = calendarRef.current?.getApi()
			if (calendarApi && calendarApi.view.type !== 'timeGridDay') {
				calendarApi.changeView('timeGridDay')
			}
		}

		mediaQuery.addEventListener('change', handleViewportChange)
		return () => mediaQuery.removeEventListener('change', handleViewportChange)
	}, [])

	// Handlers to clear selectedEvent when closing smart slot steppers
	const handleWeatherSlotStepperOpenChange = (open: boolean) => {
		if (!open) {
			setSelectedEvent(undefined)
		}
		setWeatherSlotStepperOpen(open)
	}

	const handleOccupancySlotStepperOpenChange = (open: boolean) => {
		if (!open) {
			setSelectedEvent(undefined)
		}
		setOccupancySlotStepperOpen(open)
	}

	// Helper to safely get start from FullCalendar event, falling back to rrule + duration for recurring events
	const getEventStartFromFC = (fcEvent: {
		start: Date | null
		rrule?: { dtstart?: string }
		duration?: { milliseconds?: number }
	}): Date => {
		if (fcEvent.start) return fcEvent.start
		// For recurring events, fall back to dtstart from rrule
		const rrule = (fcEvent as any).rrule
		if (rrule?.dtstart) return new Date(rrule.dtstart)
		// Last resort: current time
		return new Date()
	}

	const getEventEndFromFC = (fcEvent: {
		start: Date | null
		end: Date | null
		rrule?: { dtstart?: string }
		duration?: { milliseconds?: number }
	}): Date => {
		if (fcEvent.end) return fcEvent.end
		// For recurring events, calculate end from start + duration
		const rrule = (fcEvent as any).rrule
		const duration = (fcEvent as any).duration
		if (rrule?.dtstart && duration?.milliseconds) {
			const start = new Date(rrule.dtstart)
			return new Date(start.getTime() + duration.milliseconds)
		}
		// Last resort: start + 1 hour
		const start = getEventStartFromFC(fcEvent)
		return new Date(start.getTime() + 60 * 60 * 1000)
	}

	const handleEventClick = (info: EventClickArg) => {
		// On mobile, show desktop-only warning instead of opening edit/view flows.
		if (window.matchMedia('(max-width: 1023px)').matches) {
			showDesktopOnlyModal()
			return
		}

		// First try to get the event from our context (which has all properties)
		const eventFromContext = events.find((e) => e.id === info.event.id)

		// Safely get start/end, handling null values for recurring events
		const instanceStart = getEventStartFromFC(info.event as any)
		const instanceEnd = getEventEndFromFC(info.event as any)

		const event: CalendarEvent = eventFromContext
			? {
					...eventFromContext,
					start: instanceStart,
					end: instanceEnd,
				}
			: {
					id: info.event.id,
					title: info.event.title,
					description: info.event.extendedProps.description,
					backgroundColor: info.event.backgroundColor || '',
					start: instanceStart,
					end: instanceEnd,
					color: info.event.backgroundColor || '',
					playlists: info.event.extendedProps.playlists,
					repeatDays: info.event.extendedProps.repeatDays,
					rrule: (info.event as any).rrule, // rrule is at root level
					duration: (info.event as any).duration, // duration is at root level
					startTime: info.event.extendedProps.startTime,
					slotType: info.event.extendedProps.slotType,
					smartSlotConfig: info.event.extendedProps.smartSlotConfig,
					timeslotName: info.event.extendedProps.timeslotName,
					timeslotContents: info.event.extendedProps.timeslotContents,
					gradientColors: info.event.extendedProps.gradientColors,
					volume: info.event.extendedProps.volume,
				}

		setIsDrag(false)
		setSelectedOldEvent(event)
		setSelectedEvent(event)

		// Open the EventView modal to show event details
		setEventViewOpen(true)
	}

	const handleEventChange = (info: EventChangeArg) => {
		// FullCalendar instances for recurring slots can drop parts of the original RRULE (notably `until`).
		// Always preserve the canonical rule from our stored event when present.
		const storedEvent = findStoredEventForChange(events, info.event as any)
		const storedRRule = storedEvent?.rrule

		const event: CalendarEvent = {
			id: info.event.id,
			title: info.event.title,
			description: info.event.extendedProps.description,
			backgroundColor: info.event.backgroundColor || '',
			start: getEventStartFromFC(info.event as any),
			end: getEventEndFromFC(info.event as any),
			color: info.event.backgroundColor || '',
			playlists: info.event.extendedProps.playlists,
			repeatDays: info.event.extendedProps.repeatDays,
			rrule: mergeRRulePreserveUntil(
				storedRRule as any,
				(info.event as any).rrule
			),
			duration: (info.event as any).duration,
			startTime: info.event.extendedProps.startTime,
			slotType: info.event.extendedProps.slotType,
			smartSlotConfig: info.event.extendedProps.smartSlotConfig,
			timeslotName: info.event.extendedProps.timeslotName,
			timeslotContents: info.event.extendedProps.timeslotContents,
			gradientColors: info.event.extendedProps.gradientColors,
			volume: info.event.extendedProps.volume,
		}

		const oldEvent: CalendarEvent = {
			id: info.oldEvent.id,
			title: info.oldEvent.title,
			description: info.oldEvent.extendedProps.description,
			backgroundColor: info.oldEvent.backgroundColor || '',
			start: getEventStartFromFC(info.oldEvent as any),
			end: getEventEndFromFC(info.oldEvent as any),
			color: info.oldEvent.backgroundColor || '',
			playlists: info.oldEvent.extendedProps.playlists,
			repeatDays: info.oldEvent.extendedProps.repeatDays,
			rrule: mergeRRulePreserveUntil(
				storedRRule as any,
				(info.oldEvent as any).rrule
			),
			duration: (info.oldEvent as any).duration,
			startTime: info.oldEvent.extendedProps.startTime,
			slotType: info.oldEvent.extendedProps.slotType,
			smartSlotConfig: info.oldEvent.extendedProps.smartSlotConfig,
			timeslotName: info.oldEvent.extendedProps.timeslotName,
			timeslotContents: info.oldEvent.extendedProps.timeslotContents,
			gradientColors: info.oldEvent.extendedProps.gradientColors,
			volume: info.oldEvent.extendedProps.volume,
		}

		// Handle recurring events: revert and open modal for user to choose
		if (
			event.rrule ||
			oldEvent.rrule ||
			(event.repeatDays && event.repeatDays.length > 0) ||
			(oldEvent.repeatDays && oldEvent.repeatDays.length > 0)
		) {
			// Revert the visual change immediately
			info.revert()

			// Set isDrag flag and store event information
			setIsDrag(true)
			setSelectedOldEvent(oldEvent)
			setSelectedEvent(event)

			// Open the appropriate modal based on slot type
			if (event.slotType === 'smart' && event.smartSlotConfig) {
				if (event.smartSlotConfig.variant === 'weather') {
					setSelectedSmartType('weather')
					setWeatherSlotStepperOpen(true)
				} else if (event.smartSlotConfig.variant === 'occupation') {
					setSelectedSmartType('occupation')
					setOccupancySlotStepperOpen(true)
				}
			} else {
				setEventEditOpen(true)
			}

			return
		}

		// For non-recurring drag/drop with no overlap, just update locally (Save button will be enabled)
		const overlaps = findOverlappingEvents(
			{
				start: event.start,
				end: event.end,
				repeatDays: event.repeatDays,
			},
			events,
			event.id
		)

		// No overlap: apply change locally without opening modal
		if (overlaps.length === 0) {
			updateEvent(event.id, {
				start: event.start,
				end: event.end,
			})
			return
		}

		// If there is overlap, fall back to existing flow (modal/stepper) so user can resolve it.

		// For non-recurring occupation slots, update directly without opening modal
		if (
			event.slotType === 'smart' &&
			event.smartSlotConfig?.variant === 'occupation' &&
			(!event.repeatDays || event.repeatDays.length === 0) &&
			!event.rrule
		) {
			// Update the occupation slot directly with new start/end times
			updateEvent(event.id, {
				...event,
				start: event.start,
				end: event.end,
			})
			return
		}

		setIsDrag(true)
		setSelectedOldEvent(oldEvent)
		setSelectedEvent(event)

		// For smart slots, open the appropriate stepper; for regular slots, open edit form
		if (event.slotType === 'smart' && event.smartSlotConfig) {
			if (event.smartSlotConfig.variant === 'weather') {
				setSelectedSmartType('weather')
				setWeatherSlotStepperOpen(true)
			} else if (event.smartSlotConfig.variant === 'occupation') {
				setSelectedSmartType('occupation')
				setOccupancySlotStepperOpen(true)
			}
		} else {
			setEventEditOpen(true)
		}
	}

	const EventItem = ({ info }: EventItemProps) => {
		const { event } = info
		const [left, right] = info.timeText.split(' - ')

		// Check if this is a smart slot with gradient colors
		const gradientColors = event.extendedProps.gradientColors as
			| string[]
			| undefined
		const isSmart = event.extendedProps.slotType === 'smart'

		// Create background style
		let backgroundStyle: React.CSSProperties = {
			backgroundColor: info.backgroundColor,
		}

		// For smart slots, use solid black/white background
		const isDarkMode = document.documentElement.classList.contains('dark')
		if (isSmart) {
			backgroundStyle.backgroundColor = isDarkMode ? '#000000' : '#FFFFFF'
		}

		// Create gradient orbs for smart slots
		let fromOrbStyle: React.CSSProperties | undefined
		let toOrbStyle: React.CSSProperties | undefined

		if (isSmart && gradientColors && gradientColors.length >= 2) {
			// Create radial gradient orbs that fade to transparent
			fromOrbStyle = {
				background: `radial-gradient(circle, ${gradientColors[0]} 0%, ${gradientColors[0]}66 40%, transparent 70%)`,
				position: 'absolute',
				width: '380px',
				height: '380px',
				top: '-240px',
				left: '-240px',
				borderRadius: '50%',
				opacity: '50%',
				pointerEvents: 'none' as const,
			}

			toOrbStyle = {
				background: `radial-gradient(circle, ${gradientColors[1]} 0%, ${gradientColors[1]}66 40%, transparent 70%)`,
				position: 'absolute',
				width: '380px',
				height: '380px',
				bottom: '-240px',
				right: '-240px',
				borderRadius: '50%',
				opacity: '50%',
				pointerEvents: 'none' as const,
			}
		}

		return (
			<div className="overflow-hidden w-[full] h-full">
				{info.view.type == 'dayGridMonth' ? (
					<div
						style={backgroundStyle}
						className={
							'flex flex-col rounded-md w-full px-1 py-0.5 line-clamp-1 text-[0.4rem] sm:text-[0.5rem] md:text-[0.6rem]'
						}
					>
						<p className="font-semibold text-white line-clamp-1 leading-none">
							{event.title}
						</p>

						<p className="text-white/90 text-[0.35rem] sm:text-[0.4rem]">
							{left}
						</p>
						<p className="text-white/90 text-[0.35rem] sm:text-[0.4rem]">
							{right}
						</p>
					</div>
				) : isSmart ? (
					<div
						className="absolute flex flex-col overflow-hidden rounded-md"
						style={{
							left: isMobile ? '0.2rem' : '-0.1rem',
							right: isMobile ? '0.2rem' : '-0.1rem',
							top: isMobile ? '0.1rem' : 0,
							bottom: isMobile ? '0.1rem' : 0,
							...backgroundStyle,
						}}
					>
						{/* Gradient orbs */}
						{fromOrbStyle && <div style={fromOrbStyle} />}
						{toOrbStyle && <div style={toOrbStyle} />}

						{/* Content */}
						<div className="relative z-10 px-2 py-1">
							<div className="flex items-center gap-1 mb-1">
								<span
									className={`text-[0.5rem] flex flex-row items-center gap-1 rounded-sm px-1 sm:text-[0.6rem] font-medium ${isDarkMode ? 'bg-zinc-900/50 text-white' : 'bg-gray-100/50 text-black'}`}
								>
									<Cloud className="w-4 h-4" />
									{t('entities.schedule.calendar.smart')}
								</span>
							</div>
							<p
								className={`font-semibold w-full line-clamp-1 text-[0.6rem] sm:text-[0.7rem] md:text-xs leading-none ${isDarkMode ? 'text-white' : 'text-black'}`}
							>
								{event.title}
							</p>
							<p
								className={`line-clamp-1 text-[0.5rem] sm:text-[0.6rem] leading-none ${isDarkMode ? 'text-white/70' : 'text-black/70'}`}
							>
								{`${left} - ${right}`}
							</p>
						</div>
					</div>
				) : (
					<div
						style={backgroundStyle}
						className={`flex flex-col space-y-0 text-[0.5rem] sm:text-[0.6rem] md:text-xs rounded-md relative px-2 py-1 ${
							isMobile
								? 'w-[calc(100%-0.4rem)] h-[calc(100%-0.2rem)] mx-[0.2rem] my-[0.1rem]'
								: 'w-full h-full'
						}`}
					>
						<p className="font-semibold w-full text-white line-clamp-2 text-[0.6rem] sm:text-[0.7rem] md:text-xs leading-none">
							{event.title}
						</p>
						<p className="text-white/90 line-clamp-1 text-[0.5rem] sm:text-[0.6rem] leading-none">{`${left} - ${right}`}</p>
					</div>
				)}
			</div>
		)
	}

	const DayHeader = ({ info }: DayHeaderProps) => {
		const locale = language?.startsWith('es') ? 'es-ES' : 'en-US'
		const weekday = info.date.toLocaleDateString(locale, { weekday: 'long' })
		const dayNumber = info.date.getDate()

		return (
			<div className="flex items-center h-full overflow-hidden">
				{info.view.type == 'timeGridDay' ? (
					<div className="flex flex-col rounded-full md:rounded-sm bg-primary md:bg-transparent text-muted text-xs font-normal md:font-medium md:text-sm md:text-foreground px-3 py-1">
						<p>
							{isMobile
								? capitalizeFirstLetter(
										info.date.toLocaleDateString(locale, {
											weekday: 'long',
										})
									)
								: info.date.toLocaleDateString(locale, {
										month: 'long',
										day: 'numeric',
										year: 'numeric',
									})}
						</p>
					</div>
				) : info.view.type == 'timeGridWeek' ? (
					<div className="flex items-center justify-center w-full">
						{info.isToday ? (
							<div className="flex items-center gap-2 bg-white dark:bg-white text-black rounded-full px-4 py-1.5">
								<p className="font-medium text-xs">{weekday}</p>
								<p className="font-medium text-xs">{dayNumber}</p>
							</div>
						) : (
							<div className="flex items-center gap-2">
								<p className="font-medium text-xs text-muted-foreground">
									{weekday}
								</p>
								<p className="font-medium text-xs text-muted-foreground">
									{dayNumber}
								</p>
							</div>
						)}
					</div>
				) : (
					<div className="flex flex-col rounded-sm">
						<p className="text-foreground font-medium dark:text-white">
							{weekday}
						</p>
					</div>
				)}
			</div>
		)
	}

	const DayRender = ({ info }: DayRenderProps) => {
		return (
			<div className="flex">
				{info.view.type == 'dayGridMonth' && info.isToday ? (
					<div className="flex h-7 w-7 rounded-full bg-white items-center justify-center text-sm text-black font-medium">
						{info.dayNumberText}
					</div>
				) : (
					<div className="flex h-7 w-7 rounded-full items-center justify-center text-sm text-foreground font-medium dark:text-white">
						{info.dayNumberText}
					</div>
				)}
			</div>
		)
	}

	const handleSelectAllow = (span: DateSpanApi): boolean => {
		const startDate = span.start
		const endDate = new Date(span.end)

		endDate.setSeconds(endDate.getSeconds() - 1)

		return (
			startDate.getDate() === endDate.getDate() &&
			startDate.getMonth() === endDate.getMonth() &&
			startDate.getFullYear() === endDate.getFullYear()
		)
	}

	/**
	 * Checks for overlaps and either shows confirmation modal or proceeds to slot type selection
	 */
	const checkOverlapsAndProceed = (start: Date, end: Date) => {
		const newSlotDraft = { start, end }
		const overlaps = findOverlappingEvents(newSlotDraft, events)

		if (overlaps.length > 1) {
			// Multiple overlaps - warn and ask for confirmation before applying destructive trims/deletions
			setPendingSlotStart(start)
			setPendingSlotEnd(end)
			setMultiOverlapDetails(overlaps)
			setMultiOverlapWarningOpen(true)
			return
		}

		if (overlaps.length === 1) {
			// Single overlap - show confirmation modal
			setPendingSlotStart(start)
			setPendingSlotEnd(end)
			setOverlapDetails(overlaps[0])
			setOverlapConfirmationModalOpen(true)
			return
		}

		// No overlaps - proceed normally
		setSelectedStart(start)
		setSelectedEnd(end)
		setSelectedSlotType(null)
		setSelectedSmartType(null)
		// Allow time editing since no overlap was detected
		setIsTimeLockedFromOverlap(false)

		if (SKIP_SLOT_TYPE_SELECTION) {
			setSelectedSlotType('regular')
			setEventAddOpen(true)
		} else {
			setSlotTypeModalOpen(true)
		}
	}

	const handleDateSelect = (info: DateSelectArg) => {
		if (readOnly) return
		// On mobile, block selection and explain editing is desktop-only.
		if (window.matchMedia('(max-width: 1023px)').matches) {
			showDesktopOnlyModal()
			return
		}
		checkOverlapsAndProceed(info.start, info.end)
	}

	const handleDateClick = (info: any) => {
		if (readOnly) return
		// On mobile, block slot creation and explain editing is desktop-only.
		if (window.matchMedia('(max-width: 1023px)').matches) {
			showDesktopOnlyModal()
			return
		}
		const clickedDate = info.date
		// Keep click-to-create consistent with the calendar snapping granularity (30 minutes)
		const endDate = new Date(clickedDate.getTime() + 30 * 60 * 1000) // 30 min default
		checkOverlapsAndProceed(clickedDate, endDate)
	}

	/**
	 * Applies the overlap resolution logic to a single existing event.
	 *
	 * IMPORTANT: this mutates existing schedule segments (delete/trim/split),
	 * but does NOT close modals or proceed to the add-flow.
	 */
	const applyOverlapResolution = (details: OverlapDetails) => {
		if (!pendingSlotStart || !pendingSlotEnd) return
		applySharedOverlapResolution({
			details,
			newSlotIsRecurring: false,
			newSlotStart: pendingSlotStart,
			newSlotEnd: pendingSlotEnd,
			updateEvent,
			addEvent,
			deleteEvent,
		})
	}

	/**
	 * Handles the split confirmation from the overlap modal
	 */
	const handleConfirmSplit = () => {
		if (!overlapDetails || !pendingSlotStart || !pendingSlotEnd) return

		applyOverlapResolution(overlapDetails)

		// Close the overlap modal and proceed to slot type selection
		setOverlapConfirmationModalOpen(false)
		setOverlapDetails(null)
		setSelectedStart(pendingSlotStart)
		setSelectedEnd(pendingSlotEnd)
		setPendingSlotStart(null)
		setPendingSlotEnd(null)
		setSelectedSlotType(null)
		setSelectedSmartType(null)
		// Lock time fields since we already validated and split for this time range
		setIsTimeLockedFromOverlap(true)

		if (SKIP_SLOT_TYPE_SELECTION) {
			setSelectedSlotType('regular')
			setEventAddOpen(true)
		} else {
			setSlotTypeModalOpen(true)
		}
	}

	/**
	 * Handles cancelling the overlap confirmation
	 */
	const handleCancelOverlap = () => {
		setOverlapConfirmationModalOpen(false)
		setOverlapDetails(null)
		setPendingSlotStart(null)
		setPendingSlotEnd(null)
	}

	const handleBackMultiOverlap = () => {
		setMultiOverlapWarningOpen(false)
		setMultiOverlapDetails([])
		setPendingSlotStart(null)
		setPendingSlotEnd(null)
	}

	const handleContinueMultiOverlap = () => {
		if (!pendingSlotStart || !pendingSlotEnd) return
		if (multiOverlapDetails.length < 2) return

		// Apply overlap resolution for each overlapping existing segment
		multiOverlapDetails.forEach((details) => applyOverlapResolution(details))

		setMultiOverlapWarningOpen(false)
		setMultiOverlapDetails([])

		// Proceed to slot type selection for the pending time range
		setSelectedStart(pendingSlotStart)
		setSelectedEnd(pendingSlotEnd)
		setPendingSlotStart(null)
		setPendingSlotEnd(null)
		setSelectedSlotType(null)
		setSelectedSmartType(null)
		setIsTimeLockedFromOverlap(true)

		if (SKIP_SLOT_TYPE_SELECTION) {
			setSelectedSlotType('regular')
			setEventAddOpen(true)
		} else {
			setSlotTypeModalOpen(true)
		}
	}

	const earliestHour = getDateFromMinutes(earliestTime)
		.getHours()
		.toString()
		.padStart(2, '0')
	const earliestMin = getDateFromMinutes(earliestTime)
		.getMinutes()
		.toString()
		.padStart(2, '0')
	const latestHour = getDateFromMinutes(latestTime)
		.getHours()
		.toString()
		.padStart(2, '0')
	const latestMin = getDateFromMinutes(latestTime)
		.getMinutes()
		.toString()
		.padStart(2, '0')

	const calendarEarliestTime = `${earliestHour}:${earliestMin}`
	// FullCalendar supports slotMaxTime="24:00:00" to allow selecting/resizing up to midnight.
	// If we pass 23:59 (from latestTime=1439), the last selectable end snaps to 23:45 for 15m slots.
	const calendarLatestTime =
		latestTime === 1439 ? '24:00:00' : `${latestHour}:${latestMin}`
	const canEditSlots = !readOnly && !isMobile

	return (
		<div className="space-y-5">
			<CalendarNav
				calendarRef={calendarRef}
				start={selectedStart}
				end={selectedEnd}
				viewedDate={viewedDate}
			/>

			<Card className="p-3">
				<FullCalendar
					ref={calendarRef}
					timeZone="local"
					locale={calendarLocale}
					plugins={[
						dayGridPlugin,
						timeGridPlugin,
						multiMonthPlugin,
						interactionPlugin,
						listPlugin,
						rrulePlugin,
					]}
					initialView={initialCalendarView}
					headerToolbar={false}
					// Ensure selection/drag/resize can snap to 30-minute increments.
					slotDuration="00:30:00"
					snapDuration="00:30:00"
					slotMinTime={calendarEarliestTime}
					slotMaxTime={calendarLatestTime}
					allDaySlot={false}
					firstDay={1}
					height={'80vh'}
					contentHeight={'80vh'}
					scrollTime={new Date(new Date().getTime() - 2 * 60 * 60 * 1000)
						.toTimeString()
						.slice(0, 5)}
					displayEventEnd={true}
					windowResizeDelay={0}
					events={events.map(transformEventForFullCalendar)}
					slotLabelFormat={{
						hour: 'numeric',
						omitZeroMinute: true,
						hour12: true,
						meridiem: 'short',
					}}
					eventTimeFormat={{
						hour: 'numeric',
						minute: '2-digit',
						hour12: true,
					}}
					eventBorderColor={'black'}
					expandRows={false}
					dayCellContent={(dayInfo) => <DayRender info={dayInfo} />}
					eventContent={(eventInfo) => <EventItem info={eventInfo} />}
					dayHeaderContent={(headerInfo) => <DayHeader info={headerInfo} />}
					eventClick={(eventInfo) => handleEventClick(eventInfo)}
					eventChange={(eventInfo) => handleEventChange(eventInfo)}
					select={handleDateSelect}
					selectAllow={handleSelectAllow}
					datesSet={(dates) => {
						setViewedDate(dates.view.currentStart)
						if (isMobile && dates.view.type !== 'timeGridDay') {
							calendarRef.current?.getApi().changeView('timeGridDay')
						}
					}}
					dateClick={handleDateClick}
					nowIndicator
					editable={canEditSlots}
					eventStartEditable={canEditSlots}
					eventDurationEditable={canEditSlots}
					selectable={canEditSlots}
				/>
			</Card>
			<EventAddForm
				start={selectedStart}
				end={selectedEnd}
				displayButton={false}
				lockTimeFields={isTimeLockedFromOverlap}
			/>
			<EventEditForm
				oldEvent={selectedOldEvent}
				event={selectedEvent}
				isDrag={isDrag}
				displayButton={false}
			/>
			<EventView event={selectedEvent} readOnly={readOnly} />

			{/* Slot Type Selection Modal */}
			<SlotTypeSelectionModal
				open={slotTypeModalOpen}
				onOpenChange={setSlotTypeModalOpen}
				onSelectRegular={() => {
					setSelectedSlotType('regular')
					setSlotTypeModalOpen(false)
					setEventAddOpen(true)
				}}
				onSelectSmart={() => {
					setSelectedSlotType('smart')
					setSlotTypeModalOpen(false)
					setSmartSlotTypeModalOpen(true)
				}}
			/>

			{/* Smart Slot Type Selection Modal */}
			<SmartSlotTypeSelectionModal
				open={smartSlotTypeModalOpen}
				onOpenChange={setSmartSlotTypeModalOpen}
				onSelectWeather={() => {
					setSelectedSmartType('weather')
					setSmartSlotTypeModalOpen(false)
					setWeatherSlotStepperOpen(true)
				}}
				onSelectOccupancy={() => {
					setSelectedSmartType('occupation')
					setSmartSlotTypeModalOpen(false)
					setOccupancySlotStepperOpen(true)
				}}
			/>

			{/* Weather Smart Slot Stepper */}
			<WeatherSmartSlotStepper
				open={weatherSlotStepperOpen}
				onOpenChange={handleWeatherSlotStepperOpenChange}
				selectedDate={selectedEvent?.start || selectedStart}
				selectedStartTime={
					selectedEvent
						? `${String(selectedEvent.start.getHours()).padStart(2, '0')}:${String(selectedEvent.start.getMinutes()).padStart(2, '0')}`
						: `${String(selectedStart.getHours()).padStart(2, '0')}:${String(selectedStart.getMinutes()).padStart(2, '0')}`
				}
				selectedEndTime={
					selectedEvent
						? `${String(selectedEvent.end.getHours()).padStart(2, '0')}:${String(selectedEvent.end.getMinutes()).padStart(2, '0')}`
						: `${String(selectedEnd.getHours()).padStart(2, '0')}:${String(selectedEnd.getMinutes()).padStart(2, '0')}`
				}
				onComplete={() => {
					setWeatherSlotStepperOpen(false)
				}}
				editingEvent={selectedEvent}
				isDrag={isDrag}
			/>

			{/* Occupancy Smart Slot Stepper */}
			<OccupancySmartSlotStepper
				open={occupancySlotStepperOpen}
				onOpenChange={handleOccupancySlotStepperOpenChange}
				selectedDate={selectedEvent?.start || selectedStart}
				selectedStartTime={
					selectedEvent
						? `${String(selectedEvent.start.getHours()).padStart(2, '0')}:${String(selectedEvent.start.getMinutes()).padStart(2, '0')}`
						: `${String(selectedStart.getHours()).padStart(2, '0')}:${String(selectedStart.getMinutes()).padStart(2, '0')}`
				}
				selectedEndTime={
					selectedEvent
						? `${String(selectedEvent.end.getHours()).padStart(2, '0')}:${String(selectedEvent.end.getMinutes()).padStart(2, '0')}`
						: `${String(selectedEnd.getHours()).padStart(2, '0')}:${String(selectedEnd.getMinutes()).padStart(2, '0')}`
				}
				onComplete={() => {
					setOccupancySlotStepperOpen(false)
				}}
				editingEvent={selectedEvent}
				isDrag={isDrag}
			/>

			{/* Multiple Overlaps Warning Modal */}
			<MultiOverlapWarningModal
				open={multiOverlapWarningOpen}
				onOpenChange={(open) => {
					setMultiOverlapWarningOpen(open)
					if (!open) handleBackMultiOverlap()
				}}
				overlapCount={multiOverlapDetails.length}
				onBack={handleBackMultiOverlap}
				onContinue={handleContinueMultiOverlap}
			/>

			{/* Overlap Confirmation Modal */}
			<OverlapConfirmationModal
				open={overlapConfirmationModalOpen}
				onOpenChange={setOverlapConfirmationModalOpen}
				overlapDetails={overlapDetails}
				onConfirmSplit={handleConfirmSplit}
				onCancel={handleCancelOverlap}
			/>

			{/* Desktop Only Warning Modal */}
			<DesktopOnlyModal
				visible={showDesktopOnlyWarning}
				onClose={() => setShowDesktopOnlyWarning(false)}
			/>
		</div>
	)
}
